import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TourStep {
  target: string;
  title: string;
  content: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
  action?: string;
}

interface WelcomeTourProps {
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

export default function WelcomeTour({ isActive, onComplete, onSkip }: WelcomeTourProps) {
  const { t } = useTranslation(['app']);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="dashboard"]',
      title: t('app:welcomeTour.steps.dashboard.title'),
      content: t('app:welcomeTour.steps.dashboard.content'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="patients"]',
      title: t('app:welcomeTour.steps.patients.title'),
      content: t('app:welcomeTour.steps.patients.content'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="tests"]',
      title: t('app:welcomeTour.steps.tests.title'),
      content: t('app:welcomeTour.steps.tests.content'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="history"]',
      title: t('app:welcomeTour.steps.history.title'),
      content: t('app:welcomeTour.steps.history.content'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="evolutivos"]',
      title: t('app:welcomeTour.steps.evolutivos.title'),
      content: t('app:welcomeTour.steps.evolutivos.content'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="usage-widget"]',
      title: t('app:welcomeTour.steps.usageWidget.title'),
      content: t('app:welcomeTour.steps.usageWidget.content'),
      placement: 'left',
    },
    {
      target: '[data-tour="profile"]',
      title: t('app:welcomeTour.steps.profile.title'),
      content: t('app:welcomeTour.steps.profile.content'),
      placement: 'left',
    },
  ];

  const currentStepData = tourSteps[currentStep];

  useEffect(() => {
    if (!isActive) return;

    const updatePosition = () => {
      if (!currentStepData) return;

      const element = document.querySelector(currentStepData.target) as HTMLElement;
      if (!element) return;

      setTargetElement(element);
      const rect = element.getBoundingClientRect();

      // Calculate tooltip position based on placement
      let top = 0;
      let left = 0;

      // On mobile (width < 1024px), always center the tooltip
      const isMobile = window.innerWidth < 1024;

      if (isMobile) {
        // Center tooltip on mobile, position it below the element
        top = rect.bottom + window.scrollY + 16;
        left = window.innerWidth / 2;
      } else {
        switch (currentStepData.placement) {
          case 'bottom':
            top = rect.bottom + window.scrollY + 20;
            left = rect.left + window.scrollX + rect.width / 2;
            break;
          case 'top':
            top = rect.top + window.scrollY - 20;
            left = rect.left + window.scrollX + rect.width / 2;
            break;
          case 'left':
            top = rect.top + window.scrollY + rect.height / 2;
            left = rect.left + window.scrollX - 20;
            break;
          case 'right':
            top = rect.top + window.scrollY + rect.height / 2;
            left = rect.right + window.scrollX + 20;
            break;
        }
      }

      // Ensure tooltip stays within viewport bounds
      const tooltipWidth = isMobile ? Math.min(window.innerWidth - 32, 380) : 384;
      const tooltipHeight = tooltipRef.current?.offsetHeight ?? 220;
      const viewportTop = window.scrollY + 16;
      const viewportBottom = window.scrollY + window.innerHeight - 16;

      switch (currentStepData.placement) {
        case 'top': {
          const minTop = viewportTop + tooltipHeight;
          const maxTop = viewportBottom;
          top = Math.max(minTop, Math.min(top, maxTop));
          break;
        }
        case 'left':
        case 'right': {
          const minTop = viewportTop + tooltipHeight / 2;
          const maxTop = viewportBottom - tooltipHeight / 2;
          top = Math.max(minTop, Math.min(top, maxTop));
          break;
        }
        default: {
          const minTop = viewportTop;
          const maxTop = viewportBottom - tooltipHeight;
          top = Math.max(minTop, Math.min(top, maxTop));
          break;
        }
      }

      if (currentStepData.placement === 'left') {
        const minLeft = tooltipWidth + 16;
        const maxLeft = window.innerWidth - 16;
        left = Math.max(minLeft, Math.min(left, maxLeft));
      } else if (currentStepData.placement === 'right') {
        const minLeft = 16;
        const maxLeft = window.innerWidth - tooltipWidth - 16;
        left = Math.max(minLeft, Math.min(left, maxLeft));
      } else {
        const maxLeft = window.innerWidth - tooltipWidth / 2 - 16;
        const minLeft = tooltipWidth / 2 + 16;
        left = Math.max(minLeft, Math.min(left, maxLeft));
      }

      setPosition({ top, left });

      // Highlight the target element
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Use setTimeout to avoid infinite loop
    const timeoutId = setTimeout(updatePosition, 0);

    window.addEventListener('resize', updatePosition);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
    };
  }, [currentStep, isActive]); // Remove currentStepData from dependencies

  if (!isActive) return null;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getTooltipTransform = () => {
    switch (currentStepData.placement) {
      case 'bottom':
        return 'translate(-50%, 0)';
      case 'top':
        return 'translate(-50%, -100%)';
      case 'left':
        return 'translate(-100%, -50%)';
      case 'right':
        return 'translate(0, -50%)';
      default:
        return 'translate(-50%, 0)';
    }
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 bg-black/40 z-[9998] animate-fade-in" />

      {/* Spotlight effect on target element */}
      {targetElement && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetElement.getBoundingClientRect().top + window.scrollY - 8,
            left: targetElement.getBoundingClientRect().left + window.scrollX - 8,
            width: targetElement.offsetWidth + 16,
            height: targetElement.offsetHeight + 16,
            boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.6), 0 0 0 9999px rgba(0, 0, 0, 0.4)',
            borderRadius: '12px',
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tour tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] animate-slide-up"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: getTooltipTransform(),
          width: 'calc(100vw - 32px)',
          maxWidth: '384px',
        }}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full border-2 border-blue-500">
          {/* Arrow indicator */}
          {currentStepData.placement === 'bottom' && (
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-l-2 border-t-2 border-blue-500 rotate-45" />
          )}
          {currentStepData.placement === 'top' && (
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-r-2 border-b-2 border-blue-500 rotate-45" />
          )}
          {currentStepData.placement === 'left' && (
            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-r-2 border-t-2 border-blue-500 rotate-45" />
          )}
          {currentStepData.placement === 'right' && (
            <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-l-2 border-b-2 border-blue-500 rotate-45" />
          )}

          {/* Content */}
          <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-3 lg:mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-1 truncate">
                  {currentStepData.title}
                </h3>
                <p className="text-xs lg:text-sm text-gray-500">
                  {t('app:welcomeTour.stepOf', { current: currentStep + 1, total: tourSteps.length })}
                </p>
              </div>
              <button
                onClick={onSkip}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-2 lg:ml-4 flex-shrink-0"
              >
                <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <p className="text-sm lg:text-base text-gray-700 mb-4 lg:mb-6 leading-relaxed">
              {currentStepData.content}
            </p>

            {/* Progress bar */}
            <div className="mb-3 lg:mb-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-2 lg:gap-3">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg text-sm lg:text-base font-medium transition-all ${
                  currentStep === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <span className="hidden sm:inline">{t('app:welcomeTour.previous')}</span>
                <span className="sm:hidden">←</span>
              </button>

              <button
                onClick={handleNext}
                className="px-3 lg:px-4 py-1.5 lg:py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm lg:text-base font-semibold hover:shadow-lg transition-all"
              >
                {currentStep === tourSteps.length - 1 ? t('app:welcomeTour.done') : <><span className="hidden sm:inline">{t('app:welcomeTour.next')}</span><span className="sm:hidden">→</span></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
